import React, {
  ReactNode,
  Fragment,
  useState,
  useEffect,
  useMemo,
} from "react";
import { Combobox } from "@headlessui/react";
import { ReactComponent as ChevronDown } from "assets/chevron-down.svg";
import { ReactComponent as InfoIcon } from "assets/information-circle.svg";
import classNames from "classnames";
import { ConfirmDialog } from "components/Dialog/ConfirmDialog";
import { LoadingIndicator } from "components/LoadingIndicator/LoadingIndicator";
import Fade from "components/Transition/Fade";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { filterOptionsByQuery } from "utils/options";
import { usePopper } from "../../hooks/usePopper";

export interface Option {
  id: number | string | null;
  label: string;
  subOptions?: Option[];
  OptionComponent?: ReactNode;
}

interface ComboBoxProps<T> {
  id?: string;
  value: T | undefined;
  onChange: (option: T) => void;
  options: T[] | undefined;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  tooltipContent?: string;
  error?: string;
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
}

export const ComboBox = <TOption extends Option>({
  id,
  options,
  value,
  onChange,
  label,
  tooltipContent,
  disabled,
  error,
  loading,
  searchQuery,
  onSearchQueryChange,
}: ComboBoxProps<TOption>) => {
  const [query, setQuery] = useState("");
  const internalQuery = searchQuery ?? query;
  const setInternalQuery = onSearchQueryChange ?? setQuery;
  const { t } = useModifiedTranslation();

  const [trigger, container] = usePopper({
    placement: "bottom-start",
    modifiers: [
      {
        name: "sameWidth",
        enabled: true,
        phase: "beforeWrite",
        fn({ state }) {
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        requires: ["computeStyles"],
        effect: ({ state }) => {
          if (state.elements.reference instanceof Element) {
            setTimeout(() => {
              dispatchEvent(new CustomEvent("scroll"));
            }, 500);
            state.elements.popper.style.width = `${state.elements.reference.clientWidth}px`;
          }
        },
      },
    ],
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const filteredOptions = useMemo(() => {
    return filterOptionsByQuery(options, internalQuery);
  }, [options, internalQuery]);

  const isDisabled = disabled || options?.length === 0 || loading;

  useEffect(() => {
    if (value === undefined || value.label === "") {
      setQuery("");
    }
  }, [value]);

  return (
    <div className="flex flex-col">
      <Combobox
        id={id}
        as="div"
        value={value}
        onChange={onChange}
        disabled={isDisabled}
      >
        {label && (
          <div className="flex flex-row gap-x-2">
            <Combobox.Label
              id={!id ? undefined : `${id}-label`}
              className="text-sm font-normal"
            >
              {label}
            </Combobox.Label>
            {tooltipContent && (
              <div
                id={!id ? undefined : `${id}-tooltipDialogButton`}
                className="cursor-help"
                onClick={() => setConfirmDialogOpen(true)}
              >
                <InfoIcon />
              </div>
            )}
          </div>
        )}
        <div
          ref={trigger}
          className={classNames(
            "flex gap-2 items-center py-2.5 pr-4 w-full h-10 bg-gray-50 rounded-lg border focus-within:border-2 border-gray-300 focus-within:border-primary-400",
            {
              "border-2 border-red-500 focus-within:border-red-500": error,
            }
          )}
        >
          <Combobox.Input
            id={!id ? undefined : `${id}-input`}
            className="p-2.5 w-full h-10 text-sm text-gray-900 truncate bg-transparent rounded-lg border-0 focus:border-0 focus:ring-0 focus:-m-[1px]"
            displayValue={(option: TOption) =>
              option?.label ?? t("component.select.placeholder")
            }
            onChange={(event) => setInternalQuery(event.target.value)}
          />
          <Combobox.Button
            id={!id ? undefined : `${id}-button`}
            className={classNames("", {
              "border-gray-200 text-gray-300 cursor-not-allowed": isDisabled,
            })}
          >
            {loading ? (
              <LoadingIndicator size="xs" />
            ) : (
              <ChevronDown className="stroke-gray-500 w-[30px] h-[30px]" />
            )}
          </Combobox.Button>
        </div>

        {/* Options List */}
        <div ref={container}>
          <Combobox.Options className="overflow-y-auto py-1 max-h-64 text-base list-none bg-white rounded divide-y divide-gray-100 shadow">
            {options &&
              options.map((option, index) => (
                <li
                  key={`${option.id}-${option.label}-${index}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onChange(option)}
                  className={classNames(
                    "block py-2 pl-4 pr-4 text-sm cursor-pointer select-none text-gray-700 hover:bg-primary-900 hover:text-white",
                    {
                      "font-bold": value && option.id === (value as Option).id,
                    }
                  )}
                >
                  {option.OptionComponent ?? option.label}
                </li>
              ))}
          </Combobox.Options>
        </div>
      </Combobox>

      {error && (
        <Fade>
          <small className="mt-2 text-red-500">{error}</small>
        </Fade>
      )}
      {tooltipContent && (
        <ConfirmDialog
          id={!id ? undefined : `${id}-tooltipDialog`}
          title={t("component.select.dialogTitle")}
          description={tooltipContent}
          cancelButtonText={t("component.select.dialogCloseButtonLabel")}
          isOpen={confirmDialogOpen}
          setIsOpen={setConfirmDialogOpen}
        />
      )}
    </div>
  );
};
